import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  BarChart3,
  Activity,
  Map,
  Building,
  MessageSquare,
  X,
  Zap,
  Database,
  FileText,
  Receipt,
  Tag,
  PanelLeftClose,
  PanelLeftOpen,
  HeartPulse,
  Users
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface NavigationProps {
  isMobileMenuOpen: boolean;
  onToggleMobileMenu: () => void;
  isCollapsed: boolean;
  onToggleCollapsed: () => void;
}

// Pages a read-only 'insights' account (advisor/investor/intern) can open. Must
// match the allowedRoles in App.tsx; the backend enforces it either way.
const INSIGHTS_PATHS = [
  '/dashboard',
  '/analytics/activity-feed',
  '/analytics/action-heatmap',
  '/analytics/feedback',
  '/sessions',
  '/reports',
];

const Navigation: React.FC<NavigationProps> = ({ isMobileMenuOpen, onToggleMobileMenu, isCollapsed, onToggleCollapsed }) => {
  const location = useLocation();
  const { user } = useAuth();
  const isSessionViewer = user?.role === 'session_viewer';
  const isInsights = user?.role === 'insights';

  const navigationItems: {
    name: string;
    path: string;
    icon: React.ComponentType<{ className?: string }>;
    description: string;
    adminOnly?: boolean;
  }[] = [
    {
      name: 'Analytics',
      path: '/dashboard',
      icon: BarChart3,
      description: 'Overview, funnels & trends'
    },
    {
      name: 'Events',
      path: '/analytics/activity-feed',
      icon: Activity,
      description: 'Explore & drill into events'
    },
    {
      name: 'Action Heatmap',
      path: '/analytics/action-heatmap',
      icon: Map,
      description: 'Action density across parking lots'
    },
    {
      name: 'Parking Lots',
      path: '/parking-lots',
      icon: Building,
      description: 'Parking lot management'
    },
    {
      name: 'Feedback Analytics',
      path: '/analytics/feedback',
      icon: MessageSquare,
      description: 'Feedback analysis & insights'
    },
    {
      name: 'Live Data Controls',
      path: '/live-data-controls',
      icon: Zap,
      description: 'Real-time parking data management'
    },
    {
      name: 'Sessions',
      path: '/sessions',
      icon: Database,
      description: 'Parking session management'
    },
    {
      name: 'Receipt Requests',
      path: '/receipt-requests',
      icon: Receipt,
      description: 'Guest receipt request management'
    },
    {
      name: 'Campaigns',
      path: '/campaigns',
      icon: Tag,
      description: 'Refund campaign management'
    },
    {
      name: 'Monthly Reports',
      path: '/reports',
      icon: FileText,
      description: 'Payment reports and exports'
    },
    {
      name: 'PRS Health',
      path: '/prs-health',
      icon: HeartPulse,
      description: 'Copenhagen PRS monitoring & operations'
    },
    {
      name: 'Team',
      path: '/team',
      icon: Users,
      description: 'Admin panel access & roles',
      adminOnly: true
    }
  ];

  const handleNavClick = () => {
    // Close mobile menu when a navigation item is clicked
    if (window.innerWidth < 768) {
      onToggleMobileMenu();
    }
  };

  return (
    <>
      {/* Mobile menu overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={onToggleMobileMenu}
        />
      )}

      {/* Navigation sidebar */}
      <nav className={`
        fixed md:static inset-y-0 left-0 z-50
        ${isCollapsed ? 'w-16' : 'w-64'} bg-white shadow-lg h-full
        transform transition-transform duration-300 ease-in-out
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        <div className="p-4 h-full flex flex-col">
          {/* Mobile header with close button */}
          <div className="flex items-center justify-between mb-6 md:hidden">
            <h2 className="text-lg font-semibold text-gray-900">Menu</h2>
            <button
              onClick={onToggleMobileMenu}
              className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Desktop header */}
          <div className="hidden md:flex items-center justify-between mb-6">
            {!isCollapsed && <h2 className="text-lg font-semibold text-gray-900">Navigation</h2>}
            <button
              type="button"
              onClick={onToggleCollapsed}
              className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              title={isCollapsed ? 'Udvid menu' : 'Skjul menu'}
            >
              {isCollapsed ? <PanelLeftOpen className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
            </button>
          </div>

          {/* Navigation items */}
          <ul className="space-y-1 flex-1 overflow-y-auto">
            {navigationItems
              .filter((item) => {
                // For session_viewer, only show Sessions and Reports
                if (isSessionViewer) {
                  return item.path === '/sessions' || item.path === '/reports';
                }
                // Read-only insights accounts: the analytics pages plus Sessions and Reports
                if (isInsights) {
                  return INSIGHTS_PATHS.includes(item.path);
                }
                // Admin-only items (e.g. Team) are hidden from non-admins
                if (item.adminOnly) {
                  return user?.role === 'admin';
                }
                // Otherwise show all items
                return true;
              })
              .map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;

                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      onClick={handleNavClick}
                      className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'} px-3 py-3 rounded-lg text-sm font-medium transition-colors ${isActive
                        ? 'bg-indigo-100 text-indigo-700 border-r-2 border-indigo-500'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                        }`}
                      title={item.description}
                    >
                      <Icon className="h-4 w-4 flex-shrink-0" />
                      {!isCollapsed && <span className="truncate">{item.name}</span>}
                    </Link>
                  </li>
                );
              })}
          </ul>
        </div>
      </nav>
    </>
  );
};

export default Navigation;
